import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { ReactFlow, ReactFlowProvider, Background, useNodesState, useEdgesState, useReactFlow, Panel } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import EmployeeNode from "./EmployeeNode";
import { computeLayout } from "./layouts";
import { ZoomIn, ZoomOut, Maximize } from "lucide-react";

const nodeTypes = { employee: EmployeeNode };

function Canvas({ employees, view, layout, collapsed, onToggle, onSelect, onMove, canReparent, focusNode }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);
  const [dropTarget, setDropTarget] = useState(null);
  const dragRef = useRef(null);
  const { fitView, setCenter, zoomIn, zoomOut, getIntersectingNodes } = useReactFlow();
  const draggable = canReparent && (layout === "classic" || layout === "horizontal");

  const computed = useMemo(
    () => computeLayout(employees, { layout, collapsed, rootEmployeeId: view?.root_employee_id }),
    [employees, layout, collapsed, view?.root_employee_id]
  );

  useEffect(() => {
    setNodes(
      computed.nodes.map((n) => ({
        ...n,
        draggable,
        data: {
          ...n.data, config: view, onToggle, onSelect,
          horizontal: layout === "horizontal",
          highlighted: focusNode?.id === n.id,
          dropTarget: dropTarget === n.id,
        },
      }))
    );
    setEdges(computed.edges);
  }, [computed, view, onToggle, onSelect, draggable, layout, focusNode, dropTarget, setNodes, setEdges]);

  useEffect(() => {
    const t = setTimeout(() => fitView({ padding: 0.15, duration: 400 }), 80);
    return () => clearTimeout(t);
  }, [layout, view?.id, fitView]);

  useEffect(() => {
    if (!focusNode) return;
    const n = computed.nodes.find((x) => x.id === focusNode.id);
    if (n) setCenter(n.position.x + 116, n.position.y + 48, { zoom: 1.1, duration: 500 });
  }, [focusNode, computed.nodes, setCenter]);

  const onNodeDragStart = useCallback((_, node) => {
    dragRef.current = { id: node.id, position: { ...node.position } };
  }, []);

  const onNodeDrag = useCallback((_, node) => {
    const hits = getIntersectingNodes(node).filter((n) => n.id !== node.id);
    setDropTarget(hits.length ? hits[0].id : null);
  }, [getIntersectingNodes]);

  const onNodeDragStop = useCallback((_, node) => {
    const orig = dragRef.current;
    const target = dropTarget;
    setDropTarget(null);
    dragRef.current = null;
    if (target && target !== node.id) {
      onMove(node.id, target);
    }
    if (orig) {
      setNodes((nds) => nds.map((n) => (n.id === orig.id ? { ...n, position: orig.position } : n)));
    }
  }, [dropTarget, onMove, setNodes]);

  return (
    <ReactFlow
      nodes={nodes} edges={edges} nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      onNodeDragStart={onNodeDragStart} onNodeDrag={onNodeDrag} onNodeDragStop={onNodeDragStop}
      minZoom={0.08} maxZoom={2} proOptions={{ hideAttribution: true }}
      nodesConnectable={false} elementsSelectable zoomOnScroll panOnDrag
    >
      <Background color="#dfe2ef" gap={22} size={1.5} />
      <Panel position="bottom-right">
        <div className="flex flex-col gap-1 p-1 rounded-xl bg-white/85 backdrop-blur-xl border border-white/50 shadow-lg">
          <button data-testid="zoom-in-btn" onClick={() => zoomIn()} className="w-9 h-9 rounded-lg hover:bg-[#F0F3FF] flex items-center justify-center text-[#4b4e63]"><ZoomIn size={17} /></button>
          <button data-testid="zoom-out-btn" onClick={() => zoomOut()} className="w-9 h-9 rounded-lg hover:bg-[#F0F3FF] flex items-center justify-center text-[#4b4e63]"><ZoomOut size={17} /></button>
          <button data-testid="fit-view-btn" onClick={() => fitView({ padding: 0.15, duration: 400 })} className="w-9 h-9 rounded-lg hover:bg-[#F0F3FF] flex items-center justify-center text-[#4b4e63]"><Maximize size={16} /></button>
        </div>
      </Panel>
    </ReactFlow>
  );
}

export default function ChartCanvas(props) {
  return (
    <ReactFlowProvider>
      <Canvas {...props} />
    </ReactFlowProvider>
  );
}
